import { useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotFoundResult from '../../../components/results/NotFoundResult';
import TokenExpirationController from '../../../components/TokenExpirationController';
import LoginLayout from '../../../layouts/LoginLayout';
import LoginContent from '../../../layouts/LoginLayout/LoginContent';
import TokenExpired from '../SignInPage/TokenExpired';
import SmsOtp from './SmsOtp';
import UpdateEmail from './UpdateEmail';

type State = { step: 'invalidToken' } | { step: 'updateEmail'; token: string } | { step: 'smsOtp'; token: string };

type Action =
    | { type: 'moveToSmsOTP'; token: string }
    | { type: 'moveToUpdateEmail'; token: string }
    | { type: 'moveToNotResultFound' };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'moveToSmsOTP':
            return { step: 'smsOtp', token: action.token };

        case 'moveToUpdateEmail':
            return { step: 'updateEmail', token: action.token };

        case 'moveToNotResultFound':
            return { step: 'invalidToken' };

        default:
            return state;
    }
};

export type ActionHandlers = {
    moveToSmsOTP: (token: string) => void;
    moveToUpdateEmail: (token: string) => void;
};

const VerifyEmailUpdatePage = () => {
    const locationState = useLocation().state as { token?: string; email?: string };
    const navigate = useNavigate();

    const initialState: State = useMemo(() => {
        if (!locationState?.token) {
            return { step: 'invalidToken' };
        }

        return { step: 'smsOtp', token: locationState.token };
    }, [locationState]);

    const [state, dispatch] = useReducer(reducer, initialState);

    const actions = useMemo(
        (): ActionHandlers => ({
            moveToSmsOTP: (token: string) => dispatch({ type: 'moveToSmsOTP', token }),
            moveToUpdateEmail: (token: string) => dispatch({ type: 'moveToUpdateEmail', token }),
        }),
        [dispatch]
    );

    const stepElement = (() => {
        const tokenExpiredFallback = (
            <LoginContent>
                <TokenExpired onLinkClick={() => navigate('../signIn', { replace: true })} />
            </LoginContent>
        );

        switch (state.step) {
            case 'invalidToken':
                return <NotFoundResult />;

            case 'smsOtp':
                return (
                    <TokenExpirationController fallback={tokenExpiredFallback} token={state.token}>
                        <SmsOtp actions={actions} token={state.token} />
                    </TokenExpirationController>
                );

            case 'updateEmail':
                return (
                    <TokenExpirationController fallback={tokenExpiredFallback} token={state.token}>
                        <UpdateEmail token={state.token} />
                    </TokenExpirationController>
                );

            default:
                return null;
        }
    })();

    return <LoginLayout>{stepElement}</LoginLayout>;
};

export default VerifyEmailUpdatePage;
